import { CardConfig, RowData } from "../nesting-summary-cards";
import { Injectable } from "@angular/core";
import type { NestingSummaryCardsComponent } from "../nesting-summary-cards.component";
import type { NestingCardsWorkflowDependencies } from "./nesting-cards-workflow-dependencies";

type NestingCardsStatsWorkflowContext = Pick<
  NestingSummaryCardsComponent,
  "tempSelectedCardConfig" | "tempCardConfigs"
>;
@Injectable({ providedIn: "root" })
export class NestingCardsStatsWorkflow {
  recalculateCardStats(
    _context: NestingCardsStatsWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
    rows: RowData[],
  ): void {
    const targetCard = dependencies.findTargetCardForRows(rows);
    if (!targetCard || rows.length === 0) {
      dependencies.setPlaceholderStats(targetCard);
      return;
    }

    const metrics = dependencies.collectMetrics(rows);

    targetCard.header.pplRange = dependencies.formatPplRange(metrics.ppls);
    targetCard.header.beerType = metrics.beerTypes.size;
    targetCard.stats = [
      { label: "# Brand", value: metrics.brands.size || "-" },
      { label: "# Segment", value: metrics.segments.size || "-" },
      { label: "# Pack Type", value: metrics.packTypes.size || "-" },
      { label: "# Pack Size", value: metrics.packSizes.size || "-" },
    ];
  }
  findTargetCardForRows(
    context: NestingCardsStatsWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    rows: RowData[],
  ): CardConfig | null {
    if (
      context.tempSelectedCardConfig &&
      context.tempSelectedCardConfig.rows === rows
    ) {
      return context.tempSelectedCardConfig;
    }
    return context.tempCardConfigs.find((card) => card.rows === rows) || null;
  }
  setPlaceholderStats(
    _context: NestingCardsStatsWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    targetCard: CardConfig | null,
  ): void {
    if (!targetCard) return;
    targetCard.header.pplRange = "-";
    targetCard.header.beerType = "-";
    targetCard.stats = [
      { label: "# Brand", value: "-" },
      { label: "# Segment", value: "-" },
      { label: "# Pack Type", value: "-" },
      { label: "# Pack Size", value: "-" },
    ];
  }
  collectMetrics(
    _context: NestingCardsStatsWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    rows: RowData[],
  ): {
    beerTypes: Set<string>;
    segments: Set<string>;
    brands: Set<string>;
    packTypes: Set<string>;
    ppls: number[];
    packSizes: Set<number>;
  } {
    const beerTypes = new Set<string>(
      rows
        .map((r) => r.beer_type)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const segments = new Set<string>(
      rows
        .map((r) => r.ptc_segment)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const brands = new Set<string>(
      rows
        .map((r) => r.brand)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const packTypes = new Set<string>(
      rows
        .map((r) => r.pack_type)
        .filter((v) => v !== undefined && v !== null && v !== "") as string[],
    );
    const packSizes = new Set<number>(
      rows
        .map((r) => r.pack_size)
        .filter((v) => v !== undefined && v !== null) as number[],
    );

    const ppls: number[] = rows.reduce((acc: number[], row) => {
      if (row.price_per_ltr == null) return acc;
      const ppl =
        typeof row.price_per_ltr === "string"
          ? parseFloat(String(row.price_per_ltr).replace(/,/g, ""))
          : row.price_per_ltr;
      if (!isNaN(ppl)) acc.push(ppl);
      return acc;
    }, [] as number[]);

    return { beerTypes, segments, brands, packTypes, ppls, packSizes };
  }
  formatPplRange(
    _context: NestingCardsStatsWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
    ppls: number[],
  ): string {
    if (!ppls || ppls.length === 0) return "";
    const minPpl = Math.min(...ppls);
    const maxPpl = Math.max(...ppls);
    return minPpl === maxPpl ? `${minPpl}` : `${minPpl} - ${maxPpl}`;
  }
}
